import './bulk-message-patients-modal.scss';

import { useMutation } from '@apollo/client';
import { useCallback, useMemo, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { useAppContext } from '../../../../data/context/app-context';
import { useBulkPatientItems } from '../../../../data/data/bulk-messages/use-bulk-patient-items';
import { BulkMessagePatientsModalFilter } from '../../../../data/data/enums';
import { usePatientItem } from '../../../../data/data/patients/use-patient-item';
import {
  AddBulkMessagePatientsDocument,
  BaseBulkItemDataFragment,
  BulkCampaignType,
  BulkImportPatientsDocument,
  BulkPatientItemDataFragment,
  FileInput,
  GetBulkItemsDocument,
  GetBulkPatientsDocument,
  PatientItemDataFragment,
  RemoveBulkMessagePatientsDocument,
  ResendBulkMessageInviteDocument,
  SendBulkMessageInviteToAllDocument,
} from '../../../../data/types/api/graphql';
import { useFormatters } from '../../../../data/utils/formatters.utils';
import {
  FileItemData,
  FileSizeDocument,
  FileTypeExcel,
} from '../../../../data/utils/upload.utils';
import { AlertMessageType } from '../../../../elements/alert-message/alert-message';
import {
  Button,
  ButtonIcon,
  ButtonType,
} from '../../../../elements/button/button';
import { FormRow } from '../../../../elements/form/form';
import { InputUpload } from '../../../../elements/form/input/input-upload';
import { CustomFormattedMessage } from '../../../../elements/intl/custom-formatted-message';
import { Message, MessageType } from '../../../../elements/message/message';
import { Modal, ModalWidth } from '../../../../elements/modal/modal';
import { ModalPrompt } from '../../../../elements/modal/modal-prompt';
import { Tabs } from '../../../../elements/tabs/tabs';
import { InputAutoCompletePatientFieldController } from '../../../form/controllers/input-auto-complete-patient-field-controller';
import { BulkMessagesPatientList } from '../../list/bulk-messages-patient-list/bulk-messages-patient-list';
import { useBulkMessagePatientsForm } from './form/use-bulk-message-patients-form';

type BulkMessagePatientsModalProps = {
  bulkItem?: BaseBulkItemDataFragment | null;
  onSaved?: () => void;
  onCancel?: () => void;
  onClose?: () => void;
};

enum BulkMessagePatientsModalAction {
  LIST = 'LIST',
  IMPORT = 'IMPORT',
  SINGLE = 'SINGLE',
}

type ImportResult = {
  imported: number;
  failed: number;
  duplicate: number;
  duplicatePatients: PatientItemDataFragment[];
  importedPatients: PatientItemDataFragment[];
  errorMessages: { message: string; lineNumber: number }[];
};

function BulkMessagePatientsModal({
  bulkItem,
  onCancel,
  onClose,
}: BulkMessagePatientsModalProps) {
  const [action, setAction] = useState<BulkMessagePatientsModalAction>(
    BulkMessagePatientsModalAction.LIST
  );
  const { showAlertMessage, healthProviderId } = useAppContext();
  const intl = useIntl();
  const [cancelPromptOpen, setCancelPromptOpen] = useState<boolean>(false);
  const { getFullName } = useFormatters();
  const { form, formIsValid } = useBulkMessagePatientsForm();
  const [importResult, setImportResult] = useState<ImportResult | null>(null);

  const patientId = form.watch('patientId');

  const { data: patientItemData } = usePatientItem(patientId);

  const [filter, setFilter] = useState<BulkMessagePatientsModalFilter>(
    BulkMessagePatientsModalFilter.ALL
  );

  const onHandleCancel = useCallback(
    (confirmed: boolean = false) => {
      if (!confirmed && form.formState.isDirty) {
        setCancelPromptOpen(true);
      } else {
        onCancel?.();

        onClose?.();
      }
    },
    [form.formState.isDirty, onCancel, onClose]
  );

  const { data: bulkPatientItems, loading: bulkPatientItemsLoading } =
    useBulkPatientItems({ id: bulkItem?.id });

  const filteredPatients = useMemo(() => {
    if (filter === BulkMessagePatientsModalFilter.CONFIRMED) {
      return (
        bulkPatientItems?.filter(x => x.appointment?.id !== undefined) ?? []
      );
    }
    if (filter === BulkMessagePatientsModalFilter.DECLINED) {
      return bulkPatientItems?.filter(x => x.declined === true) ?? [];
    }
    if (filter === BulkMessagePatientsModalFilter.NOT_SENT) {
      return bulkPatientItems?.filter(x => !x.sent) ?? [];
    }
    if (filter === BulkMessagePatientsModalFilter.SENT) {
      return bulkPatientItems?.filter(x => x.sent) ?? [];
    }
    if (filter === BulkMessagePatientsModalFilter.PENDING) {
      return (
        bulkPatientItems?.filter(
          x => x.declined === false && x.sent === true && !x.appointment?.id
        ) ?? []
      );
    }
    return bulkPatientItems ?? [];
  }, [bulkPatientItems, filter]);

  const notSentCount = useMemo(
    () => bulkPatientItems?.filter(x => !x.sent).length,
    [bulkPatientItems]
  );

  const tabs = useMemo(() => {
    let items: { key: BulkMessagePatientsModalFilter; title: string }[] = [
      {
        key: BulkMessagePatientsModalFilter.ALL,
        title: intl.formatMessage({ id: 'bulk-messages.all' }),
      },
    ];
    if (bulkItem?.type === BulkCampaignType.Text) {
      items = [
        ...items,
        {
          key: BulkMessagePatientsModalFilter.SENT,
          title: intl.formatMessage({
            id: 'bulk-messages.sent',
          }),
        },
        {
          key: BulkMessagePatientsModalFilter.NOT_SENT,
          title: intl.formatMessage({
            id: 'bulk-messages.not-sent',
          }),
        },
      ];
    }
    if (bulkItem?.type === BulkCampaignType.BulkInvite) {
      items = [
        ...items,
        {
          key: BulkMessagePatientsModalFilter.CONFIRMED,
          title: intl.formatMessage({
            id: 'bulk-messages.confirmed',
          }),
        },
        {
          key: BulkMessagePatientsModalFilter.DECLINED,
          title: intl.formatMessage({
            id: 'bulk-messages.declined',
          }),
        },
        {
          key: BulkMessagePatientsModalFilter.PENDING,
          title: intl.formatMessage({
            id: 'bulk-messages.pending',
          }),
        },
        {
          key: BulkMessagePatientsModalFilter.NOT_SENT,
          title: intl.formatMessage({
            id: 'bulk-messages.not-sent',
          }),
        },
      ];
    }

    return items;
  }, [intl, bulkItem?.type]);

  const [addBulkMessagePatients, { loading: addBulkMessagePatientsLoading }] =
    useMutation(AddBulkMessagePatientsDocument, {
      refetchQueries: [GetBulkItemsDocument, GetBulkPatientsDocument],
    });

  const onHandleSubmit = useCallback(() => {
    setImportResult(null);
    form.handleSubmit(async () => {
      const values = form.getValues();

      try {
        const result = await addBulkMessagePatients({
          variables: {
            patientIds:
              action === BulkMessagePatientsModalAction.SINGLE
                ? [values.patientId]
                : importResult?.importedPatients.map(x => x.id) ?? [],
            bulkId: bulkItem?.id,
          },
        });

        if (result.data?.bulkCampaignPatientsAdd.boolean) {
          showAlertMessage({
            type: AlertMessageType.SUCCESS,
            message: intl.formatMessage({
              id: 'bulk-messages.patients.add.success',
            }),
          });

          form.reset();
          setAction(BulkMessagePatientsModalAction.LIST);

          // onSaved?.();

          // onClose?.();
        } else {
          throw new Error(
            'An unknown error has occurred in the server respond.'
          );
        }
      } catch (error) {
        showAlertMessage({
          type: AlertMessageType.ERROR,
          message: (error as Error).message,
        });
      }
    })();
  }, [
    action,
    addBulkMessagePatients,
    form,
    importResult?.importedPatients,
    intl,
    bulkItem?.id,
    showAlertMessage,
  ]);

  const [removeBulkMessagePatients] = useMutation(
    RemoveBulkMessagePatientsDocument,
    {
      refetchQueries: [GetBulkItemsDocument, GetBulkPatientsDocument],
    }
  );

  const [resendBulkMessageInvite] = useMutation(
    ResendBulkMessageInviteDocument,
    {
      refetchQueries: [GetBulkItemsDocument, GetBulkPatientsDocument],
    }
  );

  const onResend = useCallback(
    async (item: BulkPatientItemDataFragment) => {
      try {
        const result = await resendBulkMessageInvite({
          variables: { bulkId: bulkItem?.id, patientId: item.patient.id },
        });

        if (result.data?.bulkCampaignSendInvite) {
          showAlertMessage({
            type: AlertMessageType.SUCCESS,
            message: intl.formatMessage({
              id: 'bulk-messages.patients.send.success',
            }),
          });
        }
      } catch (error) {
        showAlertMessage({
          type: AlertMessageType.ERROR,
          message: (error as Error).message,
        });
      }
    },
    [bulkItem?.id, intl, resendBulkMessageInvite, showAlertMessage]
  );

  const onDelete = useCallback(
    async (item: BulkPatientItemDataFragment) => {
      try {
        const result = await removeBulkMessagePatients({
          variables: { bulkId: bulkItem?.id, patientIds: [item.patient.id] },
        });

        if (result.data?.bulkCampaignPatientDelete) {
          showAlertMessage({
            type: AlertMessageType.SUCCESS,
            message: intl.formatMessage({
              id: 'bulk-messages.patients.remove.success',
            }),
          });
        }
      } catch (error) {
        showAlertMessage({
          type: AlertMessageType.ERROR,
          message: (error as Error).message,
        });
      }
    },
    [removeBulkMessagePatients, bulkItem?.id, intl, showAlertMessage]
  );

  const [bulkImportPatients, { loading: bulkImportPatientsLoading }] =
    useMutation(BulkImportPatientsDocument, { fetchPolicy: 'no-cache' });

  const canImport = useMemo(
    () =>
      !addBulkMessagePatientsLoading &&
      importResult &&
      importResult?.importedPatients?.length > 0,
    [addBulkMessagePatientsLoading, importResult]
  );

  const onHandleFile = useCallback(
    async (value: FileItemData[]) => {
      const file = value[0];
      const base64Data = file.base64.includes('base64,')
        ? file.base64.split('base64,')[1]
        : file.base64;
      const result = await bulkImportPatients({
        variables: {
          bulkId: bulkItem?.id,
          file: { fileName: file.name, base64Data } as FileInput,
          test: true,
        },
      });

      if (
        result.data?.bulkCampaignPatientsImport
          .bulkCampaignPatientsImportFeedBack?.success === true
      ) {
        showAlertMessage({
          type: AlertMessageType.SUCCESS,
          message: intl.formatMessage({
            id: 'bulk-messages.patients.import.success',
          }),
        });

        setImportResult({
          imported:
            result.data.bulkCampaignPatientsImport
              .bulkCampaignPatientsImportFeedBack?.totalImported ?? 0,
          failed:
            result.data.bulkCampaignPatientsImport
              .bulkCampaignPatientsImportFeedBack?.totalFailed ?? 0,
          duplicate:
            result.data.bulkCampaignPatientsImport
              ?.bulkCampaignPatientsImportFeedBack?.totalDuplicate ?? 0,

          duplicatePatients:
            result.data.bulkCampaignPatientsImport.bulkCampaignPatientsImportFeedBack.patients
              .filter(x => x.imported === true)
              .map(x => x.patient),
          importedPatients:
            result.data.bulkCampaignPatientsImport.bulkCampaignPatientsImportFeedBack.patients
              .filter(x => x.imported === false)
              .map(x => x.patient),
          errorMessages:
            result.data.bulkCampaignPatientsImport?.bulkCampaignPatientsImportFeedBack?.errorMessages.map(
              x => ({ message: x.errorMessage, lineNumber: x.lineNumber })
            ), // todo
        });

        // auto set patientId to first item so that form validates
        const patients =
          result.data.bulkCampaignPatientsImport.bulkCampaignPatientsImportFeedBack?.patients
            .filter(x => x.imported === false)
            .map(x => x.patient);
        if (patients.length > 0) {
          form.setValue('patientId', patients[0].id);
        }
      } else {
        setImportResult(null);
      }
    },
    [bulkImportPatients, form, bulkItem?.id, intl, showAlertMessage]
  );

  const [sendBulkMessageToAll, { loading: sendBulkMessageToAllLoading }] =
    useMutation(SendBulkMessageInviteToAllDocument, {
      refetchQueries: [GetBulkItemsDocument, GetBulkPatientsDocument],
    });

  const onHandleSendAll = useCallback(async () => {
    try {
      const result = await sendBulkMessageToAll({
        variables: { bulkId: bulkItem?.id, resend: false },
      });
      if (result.data?.bulkCampaignSendInviteToAll.int) {
        showAlertMessage({
          type: AlertMessageType.SUCCESS,
          message: intl.formatMessage({
            id: 'agenda.appointment-create.success',
          }),
        });

        // onSaved?.();

        // onClose?.();
      } else {
        throw new Error('An unknown error has occurred in the server respond.');
      }
    } catch (error) {
      showAlertMessage({
        type: AlertMessageType.ERROR,
        message: (error as Error).message,
      });
    }
  }, [bulkItem?.id, intl, sendBulkMessageToAll, showAlertMessage]);

  const sendDisabled = useMemo(() => {
    if (!bulkItem?.active === true) return true;
    if (!bulkItem?.type) return true;
    if (notSentCount === 0) return true;

    if ([BulkCampaignType.BulkInvite].includes(bulkItem?.type)) {
      if (!bulkItem?.availableSlotsCount) {
        return true;
      }
      return false;
    }
    return false;
  }, [
    bulkItem?.active,
    bulkItem?.availableSlotsCount,
    bulkItem?.type,
    notSentCount,
  ]);

  return (
    <>
      <Modal
        className="bulk-message-patients-modal"
        title={<CustomFormattedMessage id="bulk-messages.patients.title" />}
        width={ModalWidth.LARGE}
        onClose={onClose}
        buttons={
          <>
            {[
              BulkMessagePatientsModalAction.SINGLE,
              BulkMessagePatientsModalAction.IMPORT,
            ].includes(action) ? (
              <Button
                type={ButtonType.SECONDARY}
                inverted
                onClick={() => {
                  setImportResult(null);
                  setAction(BulkMessagePatientsModalAction.LIST);
                }}
              >
                <CustomFormattedMessage id="common.cancel" />
              </Button>
            ) : (
              <Button
                type={ButtonType.SECONDARY}
                inverted
                onClick={() => {
                  onHandleCancel?.();
                }}
              >
                <CustomFormattedMessage id="common.cancel" />
              </Button>
            )}

            {action === BulkMessagePatientsModalAction.LIST && (
              <>
                <Button
                  disabled={!bulkItem?.active === true}
                  icon={ButtonIcon.USER_ADD}
                  onClick={() => {
                    setAction(BulkMessagePatientsModalAction.SINGLE);
                  }}
                >
                  <CustomFormattedMessage id="bulk-messages.patients.add-single" />
                </Button>
                <Button
                  disabled={!bulkItem?.active === true}
                  icon={ButtonIcon.USER_GROUP}
                  onClick={() => {
                    setAction(BulkMessagePatientsModalAction.IMPORT);
                  }}
                >
                  <CustomFormattedMessage id="bulk-messages.patients.add-import" />
                </Button>

                <Button
                  type={ButtonType.ICON_ONLY}
                  disabled={sendDisabled}
                  badge={notSentCount}
                  icon={
                    sendBulkMessageToAllLoading
                      ? ButtonIcon.LOADING
                      : ButtonIcon.PAPERPLANE
                  }
                  onClick={onHandleSendAll}
                />
              </>
            )}

            {action === BulkMessagePatientsModalAction.SINGLE && (
              <Button
                icon={ButtonIcon.NONE}
                disabled={!formIsValid || addBulkMessagePatientsLoading}
                onClick={onHandleSubmit}
              >
                <CustomFormattedMessage id="bulk-messages.patients.add-single" />
              </Button>
            )}
            {action === BulkMessagePatientsModalAction.IMPORT && (
              <Button
                icon={ButtonIcon.NONE}
                disabled={!canImport}
                onClick={onHandleSubmit}
              >
                <CustomFormattedMessage id="bulk-messages.patients.add-import" />
              </Button>
            )}
          </>
        }
      >
        {action === BulkMessagePatientsModalAction.LIST && (
          <>
            {bulkPatientItems && (
              <Tabs<BulkMessagePatientsModalFilter>
                value={filter}
                items={tabs}
                onChange={value => {
                  setFilter(value);
                }}
              />
            )}
            <div className="bulk-messages-patient-list">
              <BulkMessagesPatientList
                availableSlotsCount={bulkItem?.availableSlotsCount}
                active={bulkItem?.active}
                type={bulkItem?.type}
                loading={bulkPatientItemsLoading}
                items={filteredPatients}
                onDelete={onDelete}
                onResend={onResend}
              />
            </div>
          </>
        )}
        {action === BulkMessagePatientsModalAction.IMPORT && (
          <FormProvider {...form}>
            <FormRow>
              <InputUpload
                disabled={bulkImportPatientsLoading}
                name="upload"
                label={<CustomFormattedMessage id="common.file" />}
                fileTypes={[...FileTypeExcel]}
                fileSize={FileSizeDocument}
                onChange={value => onHandleFile(value as FileItemData[])}
              />
            </FormRow>
            <div>
              {bulkImportPatientsLoading && (
                <CustomFormattedMessage id="common.loading" />
              )}
              {importResult && (
                <div className="import-result">
                  <Message
                    type={MessageType.INFO}
                    message={
                      <CustomFormattedMessage
                        id="bulk-messages.patients.import.result"
                        values={{
                          imported: importResult.imported,
                          duplicate: importResult.duplicate,
                          failed: importResult.failed,
                        }}
                      />
                    }
                  />

                  {importResult.importedPatients.length > 0 && (
                    <>
                      <h3>
                        <CustomFormattedMessage id="bulk-messages.patients.import.result.success" />
                      </h3>
                      <ul>
                        {importResult.importedPatients.map(patient => (
                          <li
                            key={`${patient.firstName}-${patient.lastName}-${patient.birthDate}`}
                          >
                            {patient.firstName} {patient.lastName} (
                            {patient.birthDate})
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                  {importResult.duplicatePatients.length > 0 && (
                    <>
                      <h3>
                        <CustomFormattedMessage id="bulk-messages.patients.import.result.duplicate" />
                        ({importResult.duplicate})
                      </h3>
                      <ul>
                        {importResult.duplicatePatients.map(patient => (
                          <li
                            key={`${patient.firstName}-${patient.lastName}-${patient.birthDate}`}
                          >
                            {patient.firstName} {patient.lastName} (
                            {patient.birthDate})
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                  {importResult.errorMessages.length > 0 && (
                    <>
                      <h3>
                        <CustomFormattedMessage id="bulk-messages.patients.import.result.failed" />
                        ({importResult.failed})
                      </h3>
                      <ul>
                        {importResult.errorMessages.map(error => (
                          <li key={`${error.message}-${error.lineNumber}`}>
                            {error.message}
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </div>
              )}
            </div>
          </FormProvider>
        )}
        {action === BulkMessagePatientsModalAction.SINGLE && (
          <FormProvider {...form}>
            <FormRow>
              <InputAutoCompletePatientFieldController
                name="patientId"
                label={
                  <>
                    <CustomFormattedMessage id="common.patient" />
                    <small>*</small>
                  </>
                }
                placeholder={intl.formatMessage({
                  id: 'common.patients-search-placeholders',
                })}
                option={getFullName(patientItemData)}
                optionVariables={{ healthProviderId }}
              />
            </FormRow>
          </FormProvider>
        )}
      </Modal>
      {cancelPromptOpen && (
        <ModalPrompt
          onConfirm={() => onHandleCancel(true)}
          onClose={() => setCancelPromptOpen(false)}
        >
          <p>
            <CustomFormattedMessage id="common.confirm-not-saved-changes" />
          </p>
        </ModalPrompt>
      )}
    </>
  );
}

export { BulkMessagePatientsModal };
